/*
 * OrderVersionForm.tsx : Un formulaire pour ajouter ou modifier une version sur une commande.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBox,
	faCalendarAlt,
	faInfoCircle,
	faUsers,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	InputField,
	DatePickerField,
	TextAreaField,
	SelectField,
	finishFormSave,
	FormSaveActions,
	formatLocalDate,
	parseLocalDate,
	useFormDirty,
} from '../../../common';
import WorkflowLockBanner from '../../../common/components/WorkflowLockBanner/WorkflowLockBanner';
import CardHeader from 'layout/CardHeader';
import CardSection from 'layout/CardSection';

// Import Commande
import { OrderVersionData, signatoryOptions } from '../types/types';
import { orderVersionsApi } from '../services/orderVersionsApi';

// Import User
import { usersApi } from 'features/users';

// Composant OrderVersionForm
const OrderVersionForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Formulaire
	const [formData, setFormData] = React.useState<OrderVersionData>({
		id: 0,
		label: '',
		status: 'DRAFT',
		versionIndex: 0,
		businessOrder: {
			id: 0,
			referenceNumber: '',
			orderVersions: [],
		},
		orderLines: [],
		createdAt: undefined,
		createdBy: null,
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hook pour les options de sélection des utilisateurs
	const [userOptions, setUserOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingUsers, setLoadingUsers] = React.useState(true);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si un id est présent, on fetch la version de la commande
		if (id) {
			orderVersionsApi
				.get(Number(id), token)
				.then((data: OrderVersionData) => {
					setFormData(data);
					// Préremplir les selects
					if (data.assignedUser) {
						setUserOptions([
							{
								key: data.assignedUser.id.toString(),
								label:
									data.assignedUser.name?.toLocaleUpperCase() +
									' ' +
									data.assignedUser.firstName,
							},
						]);
					}
				})
				.catch((error) => {
					console.error('Erreur lors du chargement de la commande :', error);
					setLoading(false);
				})
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy loading : options du select Users
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingUsers) {
			usersApi
				.listSelect(token)
				.then((items) => {
					setUserOptions(
						items
							.filter((item) => item.id != null)
							.map((item) => ({
								key: String(item.id),
								label: String(item.label),
							}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingUsers(false));
		}
	}, [loading, loadingUsers]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Si un id est présent, mise à jour du devis, puis on navigue vers la page de la dernière version du devis
		if (id) {
			orderVersionsApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate(`/app/orderVersions/${formData.id}/manage`),
						markClean,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la mise à jour de la commande :', error)
				)
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'une nouvelle version de commande
			orderVersionsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate('/app/orders'),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/orderVersions/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la création de la commande :', error)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs et textareas
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'assignedUser') {
			const selectedOption = userOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					assignedUser: {
						id: Number(selectedOption.key),
						name: selectedOption.label.split(' ').slice(-1)[0],
						firstName: selectedOption.label.split(' ').slice(0, -1).join(' '),
					},
					generatedText: `${selectedOption.label.split(' ').slice(0, -1).join(' ')} ${selectedOption.label.split(' ').slice(-1)[0].toUpperCase()}`,
				}));
			}
			return;
		}

		if (name === 'signatory') {
			// On utilise les options de signatoryOptions définies dans le type
			const selectedOption = signatoryOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					signatory: selectedOption.label,
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Rendu du composant principal
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faBox}
					title={
						id
							? "Édition d'une version de commande" +
								(formData.businessOrder.referenceNumber
									? ' (' +
										formData.businessOrder.referenceNumber +
										(formData.versionIndex.toString()
											? '-V' + formData.versionIndex.toString()
											: '') +
										')'
									: '')
							: 'Nouvelle version de commande'
					}
					createdAt={
						formData.createdAt
							? formData.createdAt?.toLocaleString()
							: undefined
					}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="mt-3 w-full">
					<WorkflowLockBanner lock={formData.workflowLock} />
					<form
						onSubmit={handleSubmit}
						className="grid grid-cols-1 md:grid-cols-3 gap-6"
					>
						<CardSection
							icon={faInfoCircle}
							title="Informations générales"
							className="space-y-4"
						>
							<fieldset
								disabled={Boolean(formData.workflowLock?.locked)}
								className="m-0 min-w-0 space-y-4 border-0 p-0"
							>
								<InputField
									label="Libellé"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : Première version de la commande 001/25-A"
								/>
								<InputField
									label="Exercice"
									name="fiscalYear"
									value={formData.fiscalYear}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 2024"
								/>
								<InputField
									label="Votre référence"
									name="tiersReference"
									value={formData.tiersReference}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : DEVIS-2025-001"
								/>
								<InputField
									label="Notre référence"
									name="generatedText"
									value={formData.generatedText}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="Pierre - RSI"
								/>
								<TextAreaField
									label="Observations"
									name="publicNotes"
									value={formData.publicNotes}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="- 21 personnes sur 11 jours
                                                 - poste en 3x8"
								/>
							</fieldset>
						</CardSection>

						<CardSection
							icon={faUsers}
							title="Utilisateurs"
							className="space-y-4"
						>
							<fieldset
								disabled={Boolean(formData.workflowLock?.locked)}
								className="m-0 min-w-0 space-y-4 border-0 p-0"
							>
								<SelectField
									label="Pilote (interne)"
									selectedKey={formData.assignedUser?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'assignedUser',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={userOptions}
									loading={loading}
									optionsLoading={loadingUsers}
									entityHref={
										formData.assignedUser?.id
											? `/app/users/${formData.assignedUser.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											assignedUser: null,
										}))
									}
								/>
								<SelectField
									label="Pré-sélection du Signataire"
									selectedKey={formData.signatory || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'signatory',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={signatoryOptions}
									loading={false}
									optionsLoading={false}
								/>
								<InputField
									label="Signataire"
									name="signatory"
									value={formData.signatory}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : Patricia Ristorcelli"
								/>
							</fieldset>
						</CardSection>

						<CardSection
							icon={faCalendarAlt}
							title="Dates"
							className="space-y-4"
						>
							<fieldset
								disabled={Boolean(formData.workflowLock?.locked)}
								className="m-0 min-w-0 space-y-4 border-0 p-0"
							>
								<DatePickerField
									label="Date de livraison globale estimée"
									name="deliveryDate"
									value={parseLocalDate(formData.deliveryDate)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, deliveryDate: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date d'édition (à l'envoi au fournisseur)"
									name="editedAt"
									value={parseLocalDate(formData.editedAt)}
									onChange={(date) =>
										setFormData({
											...formData,
											editedAt: date ? formatLocalDate(date) : undefined,
										})
									}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date de l'accusé de réception"
									name="acknowledgedDate"
									value={
										formData.acknowledgedDate
											? new Date(formData.acknowledgedDate)
											: undefined
									}
									onChange={(date) =>
										setFormData({
											...formData,
											acknowledgedDate: date || undefined,
										})
									}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
							</fieldset>
							<DatePickerField
								label="Date de livraison effective"
								name="deliveredAt"
								value={
									formData.deliveredAt
										? new Date(formData.deliveredAt)
										: undefined
								}
								onChange={(date) =>
									setFormData({
										...formData,
										deliveredAt: date || undefined,
									})
								}
								placeholder="Sélectionnez une date"
								loading={loading}
								readOnly={
									Boolean(formData.workflowLock?.locked) &&
									formData.status === 'CANCELED'
								}
							/>
						</CardSection>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							if (id) {
								navigate(`/app/orderVersions/${id}/manage`);
							} else {
								navigate('/app/orders');
							}
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default OrderVersionForm;
